import { Chart } from "primereact/chart";
import { chartColors } from "../../utils";
import { Skeleton } from "primereact/skeleton";
import { useMemo } from "react";

type WeeklyStatData = {
  oneWeekAgo: number;
  twoWeeksAgo: number;
  threeWeeksAgo: number;
  fourWeeksAgo: number;
};

type StatTileProps = {
  label: string;
  value: string | number;
  weeklyData?: WeeklyStatData;
  deltaText?: string;
  comparisonText?: string;
  barColor?: string;
  loading?: boolean;
  formatTooltipValue?: (value: number) => string;
};

const StatTileSkeleton = () => (
  <div className="w-full">
    <Skeleton className="my-1" width="8rem" height="1rem" />
    <Skeleton className="my-1" width="4rem" height="1.5rem" />
    <Skeleton className="mt-4" width="100%" height="8rem" />
    <Skeleton className="my-1" width="6rem" height="1rem" />
    <Skeleton className="my-1" width="8rem" height="1rem" />
  </div>
);

export function StatTile({
  label,
  value,
  weeklyData,
  deltaText,
  comparisonText,
  barColor = chartColors.getLessonChartColor(),
  loading = false,
  formatTooltipValue,
}: StatTileProps) {
  const bars = weeklyData
    ? [
        weeklyData.fourWeeksAgo,
        weeklyData.threeWeeksAgo,
        weeklyData.twoWeeksAgo,
        weeklyData.oneWeekAgo,
      ]
    : [];

  // Memoize chart data to prevent unnecessary re-renders and animations
  const chartData = useMemo(
    () => ({
      labels: ["-4w", "-3w", "-2w", "-1w"],
      datasets: [
        {
          data: bars,
          backgroundColor: barColor,
          borderRadius: 1,
          barPercentage: 0.95,
          categoryPercentage: 0.99,
        },
      ],
    }),
    [bars, barColor]
  );

  // Memoize chart options to prevent unnecessary re-renders
  const chartOptions = useMemo(
    () => ({
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          enabled: true,
          displayColors: false,
          callbacks: {
            title: () => "",
            label: (context: { parsed: { y: number } }) =>
              formatTooltipValue ? formatTooltipValue(context.parsed.y) : `${context.parsed.y}`,
          },
        },
      },
      scales: { x: { display: false }, y: { display: false, beginAtZero: true } },
    }),
    [formatTooltipValue]
  );

  if (loading) return <StatTileSkeleton />;

  return (
    <div>
      <div className="text-muted">{label}</div>
      <div className="atlas-h2">{value}</div>
      {bars.length > 0 && (
        <Chart className="mt-4" type="bar" data={chartData} options={chartOptions} />
      )}
      {deltaText && <div className="mt-2 text-sm">{deltaText}</div>}
      {comparisonText && <div className="text-muted mt-2 text-sm">{comparisonText}</div>}
    </div>
  );
}
